import { Dns, ErrorOutline, InfoOutlined, Smartphone, WarningAmber } from '@mui/icons-material';
import { Box, Checkbox, Chip, ListItemText, MenuItem, Select, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ENTITIES } from '../../consts';
import messages from '../../intl/messages';
import { MOBILE_LOG_SEVERITY, WARNING_TEXT_COLOR, formatLogDetail, formatOffset, mergeRepeated } from '../../utils/mobileCameraLog';
import DataGrid from '../DataGrid';
import Switch from '../Switch';
import { CATEGORY_LIST, getCategoryMessage, getEvtMessage } from './logEvents';

const SEVERITY_WEIGHT = { [MOBILE_LOG_SEVERITY.ERROR]: 0, [MOBILE_LOG_SEVERITY.WARNING]: 1, [MOBILE_LOG_SEVERITY.INFO]: 2 };

const severityIcon = (severity) => {
	if (severity === MOBILE_LOG_SEVERITY.ERROR) return <ErrorOutline fontSize="small" sx={{ color: 'error.main' }} />;
	if (severity === MOBILE_LOG_SEVERITY.WARNING) return <WarningAmber fontSize="small" sx={{ color: WARNING_TEXT_COLOR }} />;
	return <InfoOutlined fontSize="small" sx={{ color: 'grey.500' }} />;
};

const isHeartbeat = (entry) => entry.category === 'heartbeat' || entry.evt === 'heartbeat';

const LogEventList = ({ entries, summary, selectedRange, onClearSelection, severityFilter, setSeverityFilter }) => {
	const intl = useIntl();
	const [showHeartbeats, setShowHeartbeats] = useState(false);
	const [mergeOn, setMergeOn] = useState(true);
	const [categoryFilter, setCategoryFilter] = useState([]);

	const visibleBySource = useMemo(
		() => (showHeartbeats ? entries : entries.filter((e) => !isHeartbeat(e))),
		[entries, showHeartbeats]
	);

	const severityCounts = useMemo(() => {
		const counts = { all: visibleBySource.length, error: 0, warning: 0, info: 0 };
		for (const e of visibleBySource) counts[e.severity] = (counts[e.severity] ?? 0) + 1;
		return counts;
	}, [visibleBySource]);

	const toggleSeverity = (severity) => {
		setSeverityFilter((prev) => (prev.includes(severity) ? prev.filter((s) => s !== severity) : [...prev, severity]));
	};

	const filtered = useMemo(() => {
		let result = visibleBySource;
		if (selectedRange) result = result.filter((e) => e.offsetMs >= selectedRange.start && e.offsetMs <= selectedRange.end);
		if (severityFilter.length) result = result.filter((e) => severityFilter.includes(e.severity));
		if (categoryFilter.length) result = result.filter((e) => categoryFilter.includes(e.category));
		return result;
	}, [visibleBySource, selectedRange, severityFilter, categoryFilter]);

	// position in the full (unfiltered) log - stable across filter/merge toggles, unlike a position in `filtered`
	const entryIndex = useMemo(() => new Map(entries.map((entry, index) => [entry, index])), [entries]);

	const rows = useMemo(() => {
		const source = mergeOn ? mergeRepeated(filtered) : filtered.map((e) => ({ ...e, count: 1, mergedEntries: [e], tEnd: e.t }));
		// t+evt isn't guaranteed unique - the backend only dedupes entries identical in every field
		// (t, src, evt, data), so e.g. two upload.fail at the same ms with different data both survive;
		// mergedEntries[0] is always the original entry object reference, so entryIndex resolves it
		return source.map((row) => ({
			...row,
			id: `${entryIndex.get(row.mergedEntries[0])}_${row.evt}`,
			// pre-computed (not a renderCell) so the grid's own filter/sort work against a real field value
			// instead of undefined, and so it reads pre-transform data (see rowsById comment below)
			detail: formatLogDetail(row.evt, row.data),
		}));
	}, [filtered, mergeOn, entryIndex]);

	// DataGrid.js runs rows through jsKeywordsToWords() (JSON round-trip that turns every true/false into
	// 'Ano'/'Ne' for readability) - fine for grid cells, but it would silently corrupt the raw JSON we show
	// in the detail panel below. Look entries up here, from the pre-transform rows, instead of DataGridPro's
	// own (transformed) row object.
	const rowsById = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);

	const columns = useMemo(
		() => [
			{
				field: 't',
				headerName: intl.formatMessage(messages.mobileLogColTime),
				width: 160,
				type: 'number',
				renderCell: (params) => (
					<Box>
						<div>{intl.formatTime(params.row.t, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</div>
						<Typography component="div" sx={{ fontSize: 11, color: 'text.secondary' }}>
							{formatOffset(params.row.offsetMs)}
						</Typography>
					</Box>
				),
			},
			{
				field: 'severity',
				headerName: intl.formatMessage(messages.mobileLogColSeverity),
				width: 60,
				sortComparator: (a, b) => SEVERITY_WEIGHT[a] - SEVERITY_WEIGHT[b],
				renderCell: (params) => (
					<span title={params.row.severity} aria-label={params.row.severity}>
						{severityIcon(params.row.severity)}
					</span>
				),
			},
			{
				field: 'src',
				headerName: intl.formatMessage(messages.mobileLogColSource),
				width: 70,
				renderCell: (params) => (
					<Chip
						size="small"
						variant="outlined"
						icon={params.row.src === 'server' ? <Dns fontSize="small" /> : <Smartphone fontSize="small" />}
						label={intl.formatMessage(params.row.src === 'server' ? messages.mobileLogSourceServer : messages.mobileLogSourceClient)}
					/>
				),
			},
			{
				field: 'category',
				headerName: intl.formatMessage(messages.mobileLogCategory),
				width: 130,
				renderCell: (params) => <Chip size="small" variant="outlined" label={intl.formatMessage(getCategoryMessage(params.row.category))} />,
			},
			{
				field: 'evt',
				headerName: intl.formatMessage(messages.mobileLogColEvent),
				flex: 1.4,
				renderCell: (params) => {
					const msg = getEvtMessage(params.row.evt);
					return (
						<span>
							{msg ? intl.formatMessage(msg) : <><FormattedMessage {...messages.mobileLogEvtUnknown} /> <code>{params.row.evt}</code></>}
							{params.row.count > 1 && ` (×${params.row.count})`}
						</span>
					);
				},
			},
			{
				field: 'detail',
				headerName: intl.formatMessage(messages.mobileLogColDetail),
				flex: 1,
			},
		],
		[intl]
	);

	const getDetailPanelContent = ({ row }) => {
		const original = rowsById.get(row.id) ?? row;
		return (
			<Box sx={{ p: 2, bgcolor: 'grey.50' }}>
				<Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>{original.evt}</Typography>
				{original.mergedEntries.map((entry, index) => (
					<Box key={`${entry.t}_${index}`} sx={{ mt: 1 }}>
						<Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
							{entry.time ?? new Date(entry.t).toISOString()}
						</Typography>
						<Box component="pre" sx={{ fontFamily: 'monospace', fontSize: 12, m: 0, whiteSpace: 'pre-wrap' }}>
							{entry.data ? JSON.stringify(entry.data, null, 2) : '-'}
						</Box>
					</Box>
				))}
			</Box>
		);
	};

	const dataGridConfig = {
		columns,
		gridId: ENTITIES.EXAMS_MOBILE_LOG,
		rows,
		getRowId: (row) => row.id,
		density: 'compact',
		getDetailPanelContent,
		getDetailPanelHeight: () => 'auto',
		getRowClassName: (params) =>
			params.row.severity === MOBILE_LOG_SEVERITY.ERROR
				? 'mobileLogRowError'
				: params.row.severity === MOBILE_LOG_SEVERITY.WARNING
					? 'mobileLogRowWarning'
					: '',
	};

	return (
		<Box>
			<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
				<Chip
					size="small"
					variant={severityFilter.length === 0 ? 'filled' : 'outlined'}
					color={severityFilter.length === 0 ? 'primary' : 'default'}
					label={`${intl.formatMessage(messages.mobileLogAll)} (${severityCounts.all})`}
					onClick={() => setSeverityFilter([])}
				/>
				<Chip
					size="small"
					variant={severityFilter.includes(MOBILE_LOG_SEVERITY.ERROR) ? 'filled' : 'outlined'}
					color={severityFilter.includes(MOBILE_LOG_SEVERITY.ERROR) ? 'error' : 'default'}
					icon={<ErrorOutline fontSize="small" />}
					label={`${intl.formatMessage(messages.mobileLogErrors)} (${severityCounts.error})`}
					onClick={() => toggleSeverity(MOBILE_LOG_SEVERITY.ERROR)}
				/>
				<Chip
					size="small"
					variant={severityFilter.includes(MOBILE_LOG_SEVERITY.WARNING) ? 'filled' : 'outlined'}
					icon={<WarningAmber fontSize="small" />}
					label={`${intl.formatMessage(messages.mobileLogWarnings)} (${severityCounts.warning})`}
					onClick={() => toggleSeverity(MOBILE_LOG_SEVERITY.WARNING)}
					sx={severityFilter.includes(MOBILE_LOG_SEVERITY.WARNING) ? { bgcolor: '#ffc400', color: WARNING_TEXT_COLOR } : undefined}
				/>
				<Chip
					size="small"
					variant={severityFilter.includes(MOBILE_LOG_SEVERITY.INFO) ? 'filled' : 'outlined'}
					icon={<InfoOutlined fontSize="small" />}
					label={`${intl.formatMessage(messages.mobileLogInfo)} (${severityCounts.info})`}
					onClick={() => toggleSeverity(MOBILE_LOG_SEVERITY.INFO)}
				/>

				<Switch checked={showHeartbeats} handleChange={(e) => setShowHeartbeats(e.target.checked)} label={intl.formatMessage(messages.mobileLogShowHeartbeats)} labelPlacement="end" />
				<Switch checked={mergeOn} handleChange={(e) => setMergeOn(e.target.checked)} label={intl.formatMessage(messages.mobileLogMergeRepeated)} labelPlacement="end" />

				<Select
					multiple
					displayEmpty
					size="small"
					value={categoryFilter}
					onChange={(e) => setCategoryFilter(e.target.value)}
					renderValue={(selected) =>
						selected.length ? selected.map((c) => intl.formatMessage(getCategoryMessage(c))).join(', ') : intl.formatMessage(messages.mobileLogCategory)
					}
					sx={{ minWidth: 160 }}
				>
					{CATEGORY_LIST.map((category) => (
						<MenuItem key={category} value={category}>
							<Checkbox size="small" checked={categoryFilter.includes(category)} />
							<ListItemText primary={intl.formatMessage(getCategoryMessage(category))} />
						</MenuItem>
					))}
				</Select>

				{selectedRange && (
					<Chip
						size="small"
						color="primary"
						label={`${intl.formatMessage(messages.mobileLogTimelineSelection)}: ${formatOffset(selectedRange.start)}–${formatOffset(selectedRange.end)}`}
						onDelete={onClearSelection}
						deleteIcon={<span aria-label={intl.formatMessage(messages.mobileLogClearSelection)}>✕</span>}
					/>
				)}
			</Box>
			<Box
				sx={{
					height: 420,
					'& .mobileLogRowError': { borderLeft: '4px solid', borderLeftColor: 'error.main', bgcolor: 'rgba(244,66,54,0.06)' },
					'& .mobileLogRowWarning': { borderLeft: '4px solid', borderLeftColor: 'warning.main' },
				}}
			>
				<DataGrid {...dataGridConfig} />
			</Box>
		</Box>
	);
};

export default LogEventList;
